/**
 * One menu for every dropdown in the chrome, rendered out of the layout.
 *
 * THIS EXISTS BECAUSE THERE WERE TWO COPIES OF IT. `SidebarAccount` and
 * `SidebarWorkspace` each hand-rolled an absolutely positioned panel, near
 * enough line for line, and between them they had no arrow keys, no focus
 * management and no agreement on what closes the thing. Somebody on a keyboard
 * could open either one and then had nothing to do but press Escape.
 *
 * WHY IT IS PORTALLED, WHICH IS THE PART THAT PAYS FOR ITSELF. An absolutely
 * positioned panel composites inside whatever stacking context its ancestors
 * make, so a menu can be painted through by a sticky table header that comes
 * later in the document. That defect shipped once. What stands in for a fix is
 * the last assertion in `stacking.test.ts`: no container between a menu and the
 * root may declare a z tier. That is a rule about files with nothing to do with
 * menus, and it is exactly the sort of thing a later tidy-up undoes by adding
 * `z-10` for safety. A panel rendered into `document.body` has no ancestor to
 * be trapped by, so it asks for `z-overlay` and gets it, and the constraint can
 * be retired along with the last hand-rolled menu.
 *
 * WHY THE PANEL IS PLACED FROM A RECT RATHER THAN BY CSS. Leaving the layout
 * costs the `absolute` positioning that used to do this for free, so the panel
 * is `fixed` at coordinates read off the trigger, which is the arrangement
 * `Tooltip` already uses for its bubble. A rect taken before a scroll points at
 * nothing afterwards, so a scroll or a resize closes the menu instead of
 * leaving it beside where the trigger used to be.
 *
 * WHY SO MUCH OF THIS FILE IS PLAIN FUNCTIONS. The keyboard walk and the
 * placement sums are exported on their own, and the painted half is a component
 * that takes its position as a prop. This workspace has no DOM test
 * environment, so a rule that lives inside a hook is a rule nobody can cover,
 * and `sidebar-model.ts`, `SystemBanner.model.ts` and `Tooltip.tsx` already
 * make the same trade.
 */
import type { CSSProperties, KeyboardEvent, ReactNode, RefObject } from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { useOnEscape } from '../lib/hooks'
import { Button } from './Button'
import { IconCheck, IconDotsVertical } from './icons'

// ---------------------------------------------------------------------------
// The keyboard walk
// ---------------------------------------------------------------------------

/** The four keys that move the highlight without choosing anything. */
export type MenuMove = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End'

/** What the walk needs to know about a row, and deliberately nothing else. */
export interface MenuRow {
  /**
   * A row that states its precondition instead of vanishing, which is the house
   * reading of the design: hiding a row teaches nobody why it is not there. The
   * walk steps over it and a click on it does nothing.
   */
  disabled?: boolean
}

/**
 * Where the highlight lands next, or -1 when there is nowhere to put it.
 *
 * It wraps at both ends, because a menu is a short ring and running off the
 * bottom of four items to no effect reads as a broken control rather than as a
 * boundary. `from` is allowed to be -1, which is the state just after opening,
 * and it is allowed to name a row that has since been disabled. Both mean "no
 * usable position", so the walk starts from whichever end the key implies.
 */
export function nextMenuIndex(rows: readonly MenuRow[], from: number, move: MenuMove): number {
  const usable: number[] = []
  rows.forEach((row, index) => {
    if (row.disabled !== true) usable.push(index)
  })
  if (usable.length === 0) return -1

  const first = usable[0] as number
  const last = usable[usable.length - 1] as number
  if (move === 'Home') return first
  if (move === 'End') return last

  const step = move === 'ArrowDown' ? 1 : -1
  const position = usable.indexOf(from)
  if (position === -1) return step === 1 ? first : last
  return usable[(position + step + usable.length) % usable.length] as number
}

// ---------------------------------------------------------------------------
// Closing, and where focus goes when it does
// ---------------------------------------------------------------------------

/** Everything that can shut a menu. Each is a different answer about focus. */
export type MenuCloseCause = 'escape' | 'tab' | 'select' | 'outside' | 'scroll' | 'resize'

/**
 * Whether closing this way hands focus back to the control that opened the menu.
 *
 * THE ANSWER IS NOT ALWAYS YES, WHICH IS THE WHOLE POINT OF THIS FUNCTION.
 * Escape, Tab and picking a row are the keyboard finishing with the menu, and
 * focus has to land somewhere a person can see. The panel is about to unmount,
 * and focus left on it falls to `body`, which sends the next Tab back to the
 * top of the page. Tab restores first and does not preventDefault, so the
 * browser's own Tab carries on from the trigger, exactly where it would have
 * been had the menu never opened.
 *
 * An outside click has already decided where focus belongs and pulling it back
 * would fight the pointer. A scroll or a resize is the window moving under a
 * menu nobody was necessarily using, and focusing the trigger there would
 * scroll the page back to it, undoing the scroll that closed it.
 */
export function returnsFocusToTrigger(cause: MenuCloseCause): boolean {
  return cause === 'escape' || cause === 'tab' || cause === 'select'
}

// ---------------------------------------------------------------------------
// Placement
// ---------------------------------------------------------------------------

export type MenuPlacement = 'top' | 'bottom'

/**
 * Which tier the panel paints at. `overlay` is for the chrome and for pages,
 * under a modal that opens over it. `popover` is for a menu whose trigger is
 * INSIDE a modal or a drawer, which at `overlay` would paint behind the very
 * surface it was opened from (the select shipped that defect on 2026-09-22).
 */
export type MenuLayer = 'overlay' | 'popover'
export type MenuAlign = 'start' | 'end'

/** The trigger's box in viewport coordinates. A `DOMRect` satisfies this as it is. */
export interface MenuAnchor {
  top: number
  left: number
  bottom: number
  right: number
}

export interface MenuViewport {
  width: number
  height: number
}

/**
 * The panel's fixed position, shaped as the style object it becomes.
 *
 * Exactly one of `top` and `bottom` carries a number. Opening upward is
 * anchored by `bottom` rather than by subtracting a height from the trigger's
 * top, because that height is not known until the panel has rendered and a
 * menu that jumps into place on its second frame is a menu that looks broken.
 */
export interface MenuSpot {
  left: number
  top?: number
  bottom?: number
  width: number
  maxHeight: number
}

/** The 0.375rem the brand menu already sits at, kept so nothing moves on conversion. */
export const MENU_GAP = 6

/** A panel touching the very edge of the window reads as clipped, so it never is. */
export const MENU_EDGE = 8

/**
 * The floor under `maxHeight`.
 *
 * In a window too short for the menu, a computed height of nothing would render
 * an empty panel, and an empty panel says less than one overflowing slightly.
 */
export const MENU_MIN_HEIGHT = 96

function clamp(value: number, low: number, high: number): number {
  if (high < low) return low
  return Math.min(Math.max(value, low), high)
}

/**
 * Where the panel goes, given the trigger and the window.
 *
 * It does not flip to the other side when space runs short, and that is a
 * decision rather than an omission. Both callers sit at a fixed end of a
 * full-height sidebar, so the side is known where the menu is written, and a
 * panel that silently changed which way it opened would move a row out from
 * under the pointer between one open and the next. Running short of room
 * shortens the panel instead and the rows inside it scroll.
 */
export function placeMenu(
  anchor: MenuAnchor,
  options: { placement: MenuPlacement; align: MenuAlign; width: number; viewport: MenuViewport },
): MenuSpot {
  const { placement, align, width, viewport } = options
  const wanted = align === 'end' ? anchor.right - width : anchor.left
  const left = clamp(wanted, MENU_EDGE, viewport.width - width - MENU_EDGE)

  if (placement === 'top') {
    return {
      left,
      width,
      bottom: viewport.height - anchor.top + MENU_GAP,
      maxHeight: Math.max(MENU_MIN_HEIGHT, anchor.top - MENU_GAP - MENU_EDGE),
    }
  }

  const top = anchor.bottom + MENU_GAP
  return {
    left,
    width,
    top,
    maxHeight: Math.max(MENU_MIN_HEIGHT, viewport.height - top - MENU_EDGE),
  }
}

// ---------------------------------------------------------------------------
// Rows and sections
// ---------------------------------------------------------------------------

export interface MenuItem extends MenuRow {
  /** Stable across renders. It keys the row and nothing else reads it. */
  id: string
  label: ReactNode
  /**
   * One line under the label. It is not truncated: on a disabled row the reason
   * it cannot be picked is the whole value of drawing the row, and an ellipsis
   * turns a stated precondition back into a mystery.
   */
  description?: ReactNode
  /** `warning` is for a description that is a problem rather than a detail. */
  descriptionTone?: 'muted' | 'warning'
  /** A mark at the head of the row: a line icon, an avatar, a brand mark. */
  icon?: ReactNode
  /**
   * Wrap the mark in the rounded tile the design draws, the same chip `Modal`
   * puts beside its title. Off by default, because a brand mark or an avatar is
   * already a tile and would end up with two.
   */
  iconChip?: boolean
  /**
   * Present at all makes this a `menuitemradio` and draws the tick, so a menu
   * that answers one question reads as the choice it is.
   */
  checked?: boolean
  onSelect?: () => void
  /**
   * `danger` draws the label and the glyph in the danger text colour, for the
   * row that ends something (`Delete`). It still opens a confirm.
   */
  tone?: 'danger'
}

export interface MenuSection {
  id: string
  /** A heading over the group, in sentence case. Absent means an unlabelled block. */
  label?: string
  items: readonly MenuItem[]
}

/** The rows across every section, in the order the arrow keys walk them. */
export function menuRows(sections: readonly MenuSection[]): MenuItem[] {
  return sections.flatMap((section) => [...section.items])
}

/** Where each section's first row sits in the flat walk above. */
export function sectionOffsets(sections: readonly MenuSection[]): number[] {
  const found: number[] = []
  let running = 0
  for (const section of sections) {
    found.push(running)
    running += section.items.length
  }
  return found
}

// ---------------------------------------------------------------------------
// The painted half
// ---------------------------------------------------------------------------

export interface MenuPanelProps {
  label: string
  sections: readonly MenuSection[]
  spot: MenuSpot
  /** Which row holds the roving tab stop, or -1 while no row can take one. */
  active: number
  header?: ReactNode
  footer?: ReactNode
  onChoose?: (item: MenuItem) => void
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
  elementRef?: RefObject<HTMLDivElement>
  /** How `Menu` collects the row it has to move focus onto. */
  registerRow?: (index: number, node: HTMLButtonElement | null) => void
  className?: string
  /** See `MenuProps.layer`. */
  layer?: MenuLayer
}

/**
 * The panel, separated from the portal so it can be rendered on its own.
 *
 * `role="menu"` is on the rows and not on the panel, which is the one place
 * this differs from both menus it replaces. A menu may contain menu items,
 * groups and separators, and both of the old ones put a paragraph of prose
 * inside theirs. Keeping the header and the footer outside the `menu` element
 * lets them stay without lying to a screen reader about what they are.
 */
export function MenuPanel({
  label,
  sections,
  spot,
  active,
  header,
  footer,
  onChoose,
  onKeyDown,
  elementRef,
  registerRow,
  className,
  layer = 'overlay',
}: MenuPanelProps) {
  const offsets = sectionOffsets(sections)

  return (
    <div
      ref={elementRef}
      style={spot as CSSProperties}
      onKeyDown={onKeyDown}
      className={cn(
        layer === 'overlay'
          ? 'fixed z-overlay flex flex-col'
          : 'fixed z-popover flex flex-col',
        // Measured: r8, a hairline, the lg shadow, 4 of padding round the rows.
        'overflow-hidden rounded-lg border border-line bg-surface shadow-lg',
        // The popover settle. It travels 4px and does not touch opacity,
        // because a keyframe starting at opacity 0 leaves the panel invisible
        // any time the timeline does not run.
        'animate-pop-in',
        className,
      )}
    >
      {header === undefined ? null : (
        <div className="shrink-0 border-b border-line px-3 py-2.5">{header}</div>
      )}

      <div role="menu" aria-label={label} className="min-h-0 flex-1 overflow-y-auto p-1">
        {sections.map((section, sectionIndex) => (
          <div
            key={section.id}
            role="group"
            aria-label={section.label}
            // The separator is a 1px rule with 4 above and below.
            className={cn(sectionIndex > 0 && 'mt-1 border-t border-line pt-1')}
          >
            {section.label === undefined ? null : (
              <p className="px-2.5 py-2 text-xs font-semibold text-ink-subtle">
                {section.label}
              </p>
            )}
            {section.items.map((item, itemIndex) => {
              const index = (offsets[sectionIndex] as number) + itemIndex
              const isDisabled = item.disabled === true
              const isDanger = item.tone === 'danger'
              return (
                <button
                  key={item.id}
                  ref={(node) => registerRow?.(index, node)}
                  type="button"
                  role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
                  aria-checked={item.checked}
                  /*
                   * `aria-disabled` and not the `disabled` attribute. A disabled
                   * button is dropped from the accessibility tree, and this row
                   * is drawn precisely so its precondition can be read.
                   */
                  aria-disabled={isDisabled ? true : undefined}
                  tabIndex={index === active ? 0 : -1}
                  onClick={() => onChoose?.(item)}
                  data-tone={isDanger ? 'danger' : undefined}
                  className={cn(
                    // h40 for a one-line row, taller when a description wraps.
                    'flex min-h-10 w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition',
                    'focus-visible:bg-surface-hover',
                    isDisabled
                      ? 'cursor-default text-ink-faint'
                      : isDanger
                        ? 'text-danger-text hover:bg-surface-hover'
                        : 'text-ink-secondary hover:bg-surface-hover',
                  )}
                >
                  {item.icon === undefined ? null : (
                    <span
                      className={cn(
                        'shrink-0',
                        item.iconChip === true
                          ? 'grid h-7 w-7 place-items-center rounded-md bg-surface-muted text-ink-subtle'
                          : cn('[&_svg]:!h-4 [&_svg]:!w-4', !isDisabled && !isDanger && 'text-ink-subtle'),
                      )}
                    >
                      {item.icon}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-base font-medium',
                        item.checked === true && 'text-ink',
                      )}
                    >
                      {item.label}
                    </span>
                    {item.description === undefined ? null : (
                      <span
                        className={cn(
                          'block text-xs leading-snug',
                          item.descriptionTone === 'warning'
                            ? 'text-warning-text'
                            : 'text-ink-muted',
                        )}
                      >
                        {item.description}
                      </span>
                    )}
                  </span>
                  {item.checked === true ? (
                    <IconCheck className="!h-4 !w-4 shrink-0 text-primary-600" />
                  ) : null}
                </button>
              )
            })}
          </div>
        ))}
      </div>

      {footer === undefined ? null : (
        <div className="shrink-0 border-t border-line px-3 pb-2 pt-1.5 text-2xs leading-relaxed text-ink-subtle">
          {footer}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The wired half
// ---------------------------------------------------------------------------

export interface MenuProps {
  open: boolean
  /** Called for every cause in `MenuCloseCause`. The menu holds no open state. */
  onClose: () => void
  /**
   * The control the panel hangs off and that focus goes back to. The caller
   * keeps its own trigger, because every one of them is a different shape, and
   * that trigger should carry `aria-haspopup="menu"` and `aria-expanded`.
   */
  triggerRef: RefObject<HTMLElement>
  /** Names the menu for a screen reader, since the trigger is not inside it. */
  label: string
  sections: readonly MenuSection[]
  placement?: MenuPlacement
  align?: MenuAlign
  /** In pixels, because the panel is placed by arithmetic and not by a class. */
  width?: number
  /**
   * Prose above the rows. Nothing here is on the arrow-key ring and Tab closes
   * the menu, so a control put in this slot would be reachable only by mouse.
   */
  header?: ReactNode
  /** Prose under the rows, for the note explaining what picking a row does. */
  footer?: ReactNode
  className?: string
  /** `popover` when the trigger sits inside a modal or a drawer. */
  layer?: MenuLayer
}

export function Menu({
  open,
  onClose,
  triggerRef,
  label,
  sections,
  placement = 'bottom',
  align = 'start',
  width = 256,
  header,
  footer,
  className,
  layer,
}: MenuProps) {
  const [spot, setSpot] = useState<MenuSpot | null>(null)
  const [active, setActive] = useState(-1)
  const panelRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([])

  const rows = useMemo(() => menuRows(sections), [sections])

  /*
   * THE TWO REFS BELOW ARE LOAD BEARING AND THEY LOOK LIKE TIDYING.
   *
   * Every caller will write `sections={[...]}` and `onClose={() => ...}` inline,
   * which is a new array and a new function on every render. Put either one in
   * an effect's dependencies and the effect re-runs every render. For the
   * placement effect that is not merely wasteful, it is an infinite loop: it
   * calls `setSpot` with a freshly built object, React sees new state, renders
   * again, and the dependency is new again. So the effects below depend on
   * scalars and on refs, and read the moving parts through these.
   *
   * It is the same arrangement `useOnEscape` and `useOnClickOutside` already
   * use for the handler they are given.
   */
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  const close = useCallback(
    (cause: MenuCloseCause) => {
      onCloseRef.current()
      if (returnsFocusToTrigger(cause)) triggerRef.current?.focus({ preventScroll: true })
    },
    [triggerRef],
  )

  // Before paint, so the panel is never shown at last time's coordinates.
  useLayoutEffect(() => {
    if (!open) {
      // Already null on every render but the first after a close, and React
      // bails out of a set that changes nothing, so this settles rather than
      // looping.
      setSpot(null)
      return
    }
    const anchor = triggerRef.current?.getBoundingClientRect()
    if (anchor === undefined) return
    setSpot(
      placeMenu(anchor, {
        placement,
        align,
        width,
        viewport: { width: window.innerWidth, height: window.innerHeight },
      }),
    )
    setActive(nextMenuIndex(rowsRef.current, -1, 'ArrowDown'))
  }, [open, placement, align, width, triggerRef])

  /*
   * The highlight is real focus and not `aria-activedescendant`, so a row that
   * has to scroll into view does it the way the browser already knows how.
   * `preventScroll` matters more than it looks: without it, focusing the first
   * row can scroll an ancestor, and the listener below would read that scroll
   * as the window moving and close the menu on the frame it opened.
   */
  useEffect(() => {
    if (!open || active < 0) return
    rowRefs.current[active]?.focus({ preventScroll: true })
  }, [open, active])

  useOnEscape(open, () => close('escape'))

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (panelRef.current?.contains(target) === true) return
      // The trigger is excluded on purpose. It toggles, so counting a click on
      // it as an outside click would close the menu here and reopen it in the
      // trigger's own handler, and the menu would look stuck open.
      if (triggerRef.current?.contains(target) === true) return
      close('outside')
    }

    const onScroll = (event: Event): void => {
      /*
       * Capture, so a scroll inside a pane closes this and not only a scroll of
       * the page, which is the same reason `Tooltip` listens this way. The
       * panel's own rows are the exception: a capture listener on the window
       * sees that scroll too, and closing on it would put the bottom of a long
       * list out of reach of a trackpad.
       */
      const target = event.target
      if (target instanceof Node && panelRef.current?.contains(target) === true) return
      close('scroll')
    }

    const onResize = (): void => close('resize')

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, close, triggerRef])

  const registerRow = useCallback((index: number, node: HTMLButtonElement | null) => {
    rowRefs.current[index] = node
  }, [])

  const choose = (item: MenuItem): void => {
    if (item.disabled === true) return
    close('select')
    item.onSelect?.()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Tab') {
      close('tab')
      return
    }
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'Home' ||
      event.key === 'End'
    ) {
      // Or the page scrolls under the menu while the highlight moves, and the
      // scroll listener above closes the menu the arrow key was walking.
      event.preventDefault()
      setActive(nextMenuIndex(rows, active, event.key))
    }
  }

  if (!open || spot === null) return null

  return createPortal(
    <MenuPanel
      label={label}
      sections={sections}
      spot={spot}
      active={active}
      header={header}
      footer={footer}
      onChoose={choose}
      onKeyDown={onKeyDown}
      elementRef={panelRef}
      registerRow={registerRow}
      className={className}
      layer={layer}
    />,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// The row kebab
// ---------------------------------------------------------------------------

export interface RowKebabProps {
  /** Names the button and the menu, such as `Actions for Spring launch`. */
  label: string
  /** One unlabelled section, for the usual short list. */
  items?: readonly MenuItem[]
  /** Several sections, separated by rules, when the rows fall into groups. */
  sections?: readonly MenuSection[]
  /** `popover` when the row is inside a modal or a drawer. */
  layer?: MenuLayer
  width?: number
  className?: string
}

/**
 * The three-dot button at the end of a row, and the menu it opens.
 *
 * The campaign row, exactly: `Duplicate campaign`, `Edit campaign`, a rule,
 * then `Delete` with `tone: 'danger'` (which opens a confirm). A canvas node
 * carries `Delete node` alone.
 *
 * A click on it never reaches the row. Rows that open something are links, and
 * a kebab that also navigated would open the row and the menu at once.
 */
export function RowKebab({ label, items, sections, layer, width = 200, className }: RowKebabProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const resolved = useMemo<readonly MenuSection[]>(
    () => sections ?? [{ id: 'actions', items: items ?? [] }],
    [items, sections],
  )
  return (
    <>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="xs"
        iconOnly
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setOpen((current) => !current)
        }}
        className={cn('group', className)}
      >
        <IconDotsVertical className="!h-5 !w-5 text-ink-faint group-hover:text-ink-subtle" />
      </Button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        label={label}
        sections={resolved}
        align="end"
        width={width}
        layer={layer}
      />
    </>
  )
}
